import axios from 'axios'
import { Report, MailQueue } from '@/domain/Report'
import FileData from '@/domain/FileData'
import { SendMail } from '@/domain/SendMail'

/**
 * 検索条件
 */
export class SerchConditon {
    /** 受付番号 */
    requestNo = ""
    /** 期間開始日 */
    startOn = ""
    /** 期間終了日 */
    endOn = ""
    /** 顧客都道府県コード */
    customerPrefectureCode = ""
    /** 顧客コード */
    customerCode = ""
    /** 顧客枝番 */
    customerBranchNumber = ""
    /** 顧客名 */
    customerName = ""
    /** 検査依頼種別 */
    requestType: number | null = null
    /** 検査依頼の備考 */
    requestNote = ""
    /** CSV連携対象のみ */
    isCsvOnly = false
    /* 有効 */
    isActive = true

    /* 画面用 */
    /** 期間タイプ */
    dateType = 3

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<SerchConditon>) {
        Object.assign(this, init)
    }
}

/**
 * 送信日情報
 */
export class SendInfo {
    /** 報告書No */
    reportNos: string[] = []
    /** 送信日 */
    date = ""

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<SendInfo>) {
        Object.assign(this, init)
    }
}

/**
 * 検査一覧サービス
 */
export default class ReportsService {
    /** インスタンス */
    private static _instance: ReportsService

    /**
     * コンストラクタ
     */
    // eslint-disable-next-line
    private constructor() {}

    /**
     * インスタンスの取得
     */
    public static get instance() {
        if (!this._instance) {
            this._instance = new ReportsService();
        }
        return this._instance;
    }

    /**
     * 新規作成
     */
    public async createNew(): Promise<Report> {
        return await axios.get("/api/report/new")
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 参照新規作成
     */
    public async referenceNew(requestNo: string): Promise<Report> {
        return await axios.get("/api/report/new", {params: {requestNo: requestNo}})
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 編集
     */
    public async edit(no: string): Promise<Report> {
        return await axios.get(`/api/report/edit/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 新規保存
     */
    public async create(dto: Report): Promise<Report> {
        return await axios.post("/api/report/new", dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 保存
     */
    public async update(dto: Report): Promise<Report> {
        return await axios.put(`/api/report/edit/${dto.reportNo}`, dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 削除
     */
    public async delete(no: string): Promise<Report> {
        return await axios.delete(`/api/report/delete/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 報告書一覧を検索
     */
    public async findAll(serchConditon: SerchConditon): Promise<Report[]> {
        return await axios.get("/api/reports", {params: serchConditon})
            .then((results) => {
                if (results.status === 200) {
                    return (results.data as Report[]).map(x => this.convert(x))
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 報告書ダウンロード
     */
    public async download(reportNo: string): Promise<FileData> {
        return await axios.get(`/api/report/download/${reportNo}`)
            .then((results) => {
                if (results.status === 200) {
                    return new FileData({ ...(results.data)})
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * メール送信テンプレートの取得
     */
    public async generateMailTemplate(nos: string[]): Promise<SendMail> {
        const params = nos.map(x => `no=${x}`).join("&")
        return await axios.get(`/api/reports/generate_mail_template?${params}`)
            .then((results) => {
                if (results.status === 200) {
                    const sendMail = results.data as SendMail
                    return new SendMail({ ...sendMail })
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * メール送信テンプレートの取得
     */
    public async generateMailTemplateByRequest(nos: string[]): Promise<SendMail> {
        const params = nos.map(x => `no=${x}`).join("&")
        return await axios.get(`/api/reports/generate_mail_template_by_request?${params}`)
            .then((results) => {
                if (results.status === 200) {
                    const sendMail = results.data as SendMail
                    return new SendMail({ ...sendMail })
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * メール送信キューへの登録
     */
    public async mailQueuePush(mailQueue: MailQueue): Promise<MailQueue> {
        return await axios.post("/api/reports/mail_queue_push", mailQueue)
            .then((results) => {
                if (results.status === 200) {
                    return results.data as MailQueue
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 報告書郵送日情報更新
     */
    public async sendPost(reportNos: string[], date: string): Promise<number> {
        const params = new SendInfo({reportNos: reportNos, date: date})
        return await axios.put("/api/reports/post_send_on", params)
            .then((results) => {
                if (results.status === 200) {
                    return results.data as number
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 報告書FAX送信日情報更新
     */
    public async sendFax(reportNos: string[], date: string): Promise<number> {
        const params = new SendInfo({reportNos: reportNos, date: date})
        return await axios.put("/api/reports/fax_send_on", params)
            .then((results) => {
                if (results.status === 200) {
                    return results.data as number
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 受信データの変換
     * @param data
     */
    // eslint-disable-next-line
    private convert(data: any): Report {
        const report = data as Report

        return new Report({ ...report })
    }
}
